import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Brain, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { api, type MindmapSummary } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export default function Mapy() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [title, setTitle] = useState("");

  const listQ = useQuery({
    queryKey: ["mindmaps", q],
    queryFn: () => api.mindmapsList(q.trim() || undefined),
  });

  const createM = useMutation({
    mutationFn: () =>
      api.mindmapsCreate({
        title: title.trim() || "Nowa mapa",
        description: "",
      }),
    onSuccess: (res) => {
      setTitle("");
      void qc.invalidateQueries({ queryKey: ["mindmaps"] });
      navigate(`/mapy/${res.map.id}`);
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.mindmapsDelete(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["mindmaps"] }),
  });

  const items: MindmapSummary[] = listQ.data?.items || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mapy myśli"
        subtitle="Warstwa ponad Kanbanem: koncepcje, systemy, hipotezy i postęp. Kanban zostaje do egzekucji."
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            className="pl-8"
            placeholder="Szukaj map…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Input
          className="max-w-xs"
          placeholder="Tytuł nowej mapy"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") createM.mutate();
          }}
        />
        <Button onClick={() => createM.mutate()} disabled={createM.isPending}>
          {createM.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          <span className="ml-1.5">Nowa mapa</span>
        </Button>
      </div>

      {listQ.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Ładowanie…
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500">Brak map. Utwórz pierwszą albo odśwież seed.</p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((m) => (
            <li key={m.id}>
              <div
                className={cn(
                  "group relative rounded-xl border bg-white p-4 shadow-sm transition hover:border-indigo-300",
                  m.is_hub && "border-indigo-200 bg-indigo-50/40",
                )}
              >
                <Link to={`/mapy/${m.id}`} className="block pr-8">
                  <div className="mb-2 flex items-center gap-2">
                    <Brain className="h-4 w-4 text-indigo-500" />
                    <h2 className="font-medium text-slate-900">{m.title}</h2>
                    {m.is_hub ? <Badge variant="secondary">Hub</Badge> : null}
                  </div>
                  {m.description ? (
                    <p className="mb-3 line-clamp-2 text-sm text-slate-600">{m.description}</p>
                  ) : null}
                  <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                    <span>Postęp {Math.round(m.progress)}%</span>
                    <span>{m.updated_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-indigo-500"
                      style={{ width: `${Math.min(100, Math.max(0, m.progress))}%` }}
                    />
                  </div>
                </Link>
                <button
                  type="button"
                  className="absolute right-2 top-2 rounded p-1 text-slate-400 opacity-0 hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                  title="Usuń mapę"
                  onClick={() => {
                    if (window.confirm(`Usunąć mapę „${m.title}”?`)) deleteM.mutate(m.id);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
